import { Badge, Switch } from "@chakra-ui/react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { IFilterPaymentDetail } from "./interface";
import {
  PaginatorInfo,
  PaymentDetail,
  useListPaymentDetailsQuery,
} from "../../generated/graphql";
import { createColumnHelper } from "@tanstack/react-table";
import Filter from "./Filter";
import update from "immutability-helper";
import DefaultTable, {
  TableImperativeHandle,
  ExtendedDefaultTableProps,
} from "../DefaultTable";
import { useConfirmation } from "../../contexts/ConfirmationContext";

const columnHelper = createColumnHelper<PaymentDetail>();

const TablePaymentDetail = forwardRef<
  TableImperativeHandle<PaymentDetail>,
  ExtendedDefaultTableProps<PaymentDetail, IFilterPaymentDetail> & {
    onChangeDefault?: (row: PaymentDetail) => void;
    activeOnly?: boolean;
  }
>(
  (
    {
      filterParams,
      onFilterSubmit,
      page,
      onPageChange,
      onChangeDefault,
      activeOnly,
      ...props
    },
    ref
  ) => {
    useImperativeHandle(ref, () => ({
      updateRow(data) {
        setDatas((datas) => {
          const index = datas.findIndex((elm) => elm.id === data.id);
          return update(datas, {
            [index]: {
              $set: { ...datas[index], ...data },
            },
          });
        });
      },
      refresh() {
        refetch();
      },
    }));

    const [localFilterParams, setLocalFilterParams] =
      useState<IFilterPaymentDetail>();
    const [localPage, setLocalPage] = useState<number>(1);
    const [datas, setDatas] = useState<PaymentDetail[]>([]);

    const columns = useMemo(() => {
      return [
        columnHelper.accessor("companyName", {
          header: () => "Company Name",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("branchName", {
          header: () => "Branch Name",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("bankNo", {
          header: () => "Bank No",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("swiftCode", {
          header: () => "Swift Code",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("accountNo", {
          header: () => "Account No",
          cell: (info) => info.getValue(),
        }),
        columnHelper.accessor("isDefault", {
          header: () => "Default",
          cell: (info) => (
            <Switch
              isChecked={info.getValue()}
              onChange={() =>
                onChangeDefault && onChangeDefault(info.row.original)
              }
              readOnly={info.getValue()}
            />
          ),
        }),
        columnHelper.accessor("enable", {
          header: () => "Enabled",
          cell: (info) => (
            <Badge colorScheme={info.getValue() ? "green" : "red"}>
              {info.getValue() ? "Enabled" : "Disabled"}
            </Badge>
          ),
        }),
      ];
    }, []);

    useEffect(() => {
      if (filterParams !== undefined) setLocalFilterParams(filterParams);
    }, [filterParams]);

    useEffect(() => {
      if (page !== undefined) setLocalPage(page);
    }, [page]);

    useEffect(() => {
      setLocalFilterParams({ ...localFilterParams });
    }, []);

    const [paginator, setPaginator] = useState<PaginatorInfo>();

    const { data, loading, refetch } = useListPaymentDetailsQuery({
      notifyOnNetworkStatusChange: true,
      variables: {
        key: localFilterParams?.key || undefined,
        enable: activeOnly ? true : undefined
      },
    });

    const [isLazyLoading, setIsLazyLoading] = useState(true);

    useEffect(() => {
      setPaginator(data?.listPaymentDetails.paginatorInfo);
      setDatas((data?.listPaymentDetails.data || []) as PaymentDetail[]);
    }, [data]);

    useEffect(() => {
      setIsLazyLoading(loading);
    }, [loading, setIsLazyLoading]);

    useEffect(() => {
      refetch({
        page: localPage,
        key: localFilterParams?.key || undefined,
      });
    }, [localFilterParams, localPage, refetch]);

    return (
      <DefaultTable
        data={datas}
        columns={columns}
        isLoading={loading || isLazyLoading}
        getRowId={(row) => row.id}
        filterComponent={
          <Filter
            value={localFilterParams}
            onSubmit={(data) => {
              setLocalPage(1);
              setLocalFilterParams(data);
              onFilterSubmit && onFilterSubmit(data);
            }}
          />
        }
        paginator={paginator}
        onPageChange={(page) => {
          setLocalPage(page);
          onPageChange && onPageChange(page);
        }}
        {...props}
      />
    );
  }
);

export default TablePaymentDetail;
